"use client";

import { useState } from "react";
import { Loader2, Shield, Key, Send } from "lucide-react";

import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";

interface SimplifiedMFAModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: () => void;
  onVerify: (code: string, method: "totp" | "sms") => Promise<boolean>;
  onSendSMS?: () => Promise<void>;
  title?: string;
  description?: string;
}

export const SimplifiedMFAModal = function ({
  isOpen,
  onClose,
  onVerified,
  onVerify,
  onSendSMS,
  title = "Verify Your Identity",
  description = "Please verify your identity with 2FA to continue",
}: SimplifiedMFAModalProps) {
  const [verificationCode, setVerificationCode] = useState("");
  const [selectedMethod, setSelectedMethod] = useState<"totp" | "sms">("totp");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleSendSMS = async () => {
    if (!onSendSMS) return;

    setIsSendingSMS(true);
    setError(null);

    try {
      await onSendSMS();
      setSmsSent(true);
      setSmsCountdown(60);

      // Start countdown
      const interval = setInterval(() => {
        setSmsCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      setError(err.message || "Failed to send SMS code");
    } finally {
      setIsSendingSMS(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode.trim()) {
      setError("Please enter a verification code");
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const success = await onVerify(verificationCode, selectedMethod);

      if (success) {
        onVerified();
        handleClose();
      } else {
        setError("Invalid verification code. Please try again.");
      }
    } catch (err: any) {
      setError(err.message || "Verification failed. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClose = () => {
    setVerificationCode("");
    setError(null);
    setIsVerifying(false);
    setSmsSent(false);
    setSmsCountdown(0);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isVerifying) {
      handleVerify();
    }
  };

  const handleMethodChange = (value: string) => {
    setSelectedMethod(value as "totp" | "sms");
    setVerificationCode("");
    setError(null);
    setSmsSent(false);
    setSmsCountdown(0);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <ResponsiveDialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Method Selection */}
          <div className="space-y-2">
            <Label htmlFor="method">Verification Method</Label>
            <Select
              value={selectedMethod}
              onValueChange={handleMethodChange}
            >
              <SelectTrigger id="method">
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="totp">
                  <div className="flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    Authenticator App (TOTP)
                  </div>
                </SelectItem>
                <SelectItem value="sms">
                  <div className="flex items-center gap-2">
                    📱
                    SMS Code
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* SMS Send Button */}
          {selectedMethod === "sms" && onSendSMS && (
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleSendSMS}
                disabled={isSendingSMS || smsCountdown > 0}
                className="w-full"
              >
                {isSendingSMS ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : smsCountdown > 0 ? (
                  <>
                    Resend in {smsCountdown}s
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    {smsSent ? "Resend SMS Code" : "Send SMS Code"}
                  </>
                )}
              </Button>
              {smsSent && smsCountdown > 0 && (
                <p className="text-sm text-green-600">
                  SMS code sent successfully! Check your phone.
                </p>
              )}
            </div>
          )}

          {/* Verification Code Input */}
          <div className="space-y-2">
            <Label htmlFor="code">Verification Code</Label>
            <Input
              id="code"
              type="text"
              placeholder={
                selectedMethod === "totp"
                  ? "Enter 6-digit code from app"
                  : "Enter 6-digit SMS code"
              }
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                setVerificationCode(value);
                setError(null);
              }}
              onKeyPress={handleKeyPress}
              maxLength={6}
              autoFocus
              disabled={isVerifying || (selectedMethod === "sms" && !smsSent)}
              className={error ? "border-red-500" : ""}
            />
            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                ⚠️ {error}
              </p>
            )}
          </div>

          {/* Help Text */}
          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="font-medium text-blue-900 mb-1">Need help?</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              {selectedMethod === "totp" && (
                <li>Open your authenticator app and enter the 6-digit code</li>
              )}
              {selectedMethod === "sms" && (
                <>
                  <li>Click &quot;Send SMS Code&quot; to receive a code</li>
                  <li>Check your phone for the SMS with the 6-digit code</li>
                </>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isVerifying}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleVerify}
            disabled={isVerifying || !verificationCode.trim() || (selectedMethod === "sms" && !smsSent)}
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <Shield className="mr-2 h-4 w-4" />
                Verify & Continue
              </>
            )}
          </Button>
        </DialogFooter>
      </ResponsiveDialogContent>
    </Dialog>
  );
};
